import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Plus, Upload, RefreshCw, Trash2, Pencil, Search, Package, Wallet, CalendarClock, Activity, AlertTriangle,
} from "lucide-react";
import { api } from "../auth/api";
import { useAuth } from "../auth/AuthContext";
import {
  PageHeader, Card, Stat, Badge, Button, Modal, Field, inputClass, Spinner, ErrorNote, Empty,
} from "../components/ui";

const KINDS = ["SaaS", "Desktop", "Self-hosted", "Hardware"];
const money = (n, cur = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

const statusBadge = (s) =>
  s === "up" ? <Badge tone="good">Online</Badge> : s === "down" ? <Badge tone="bad">Down</Badge> : <Badge>Not monitored</Badge>;

// Maps spreadsheet / form column headings to our fields by keyword.
const COLUMN_RULES = [
  ["name", /(software|tool|app|application|name)/i],
  ["category", /(categor|type of|purpose)/i],
  ["vendor", /(vendor|provider|publisher|company)/i],
  ["kind", /(kind|deployment|hosting|platform)/i],
  ["owner", /(owner|responsible|contact|admin)/i],
  ["department", /(department|team|used by)/i],
  ["seats", /(seat|users|licen[cs]es|count)/i],
  ["monthlyCost", /(cost|price|amount|spend|fee)/i],
  ["renewalDate", /(renew|expiry|expires|due)/i],
  ["url", /(url|link|website|address)/i],
  ["notes", /(note|remark|comment|description)/i],
];

function mapRows(json) {
  if (!json.length) return [];
  const headers = Object.keys(json[0]);
  const used = new Set();
  const map = {};
  for (const [field, rx] of COLUMN_RULES) {
    const h = headers.find((x) => !used.has(x) && rx.test(x));
    if (h) { map[field] = h; used.add(h); }
  }
  return json
    .map((r) => {
      const out = {};
      for (const [f, h] of Object.entries(map)) out[f] = r[h];
      if (out.renewalDate instanceof Date) out.renewalDate = out.renewalDate.toISOString().slice(0, 10);
      if (out.kind && !KINDS.includes(out.kind)) delete out.kind;
      if (typeof out.monthlyCost === "string") out.monthlyCost = Number(out.monthlyCost.replace(/[^\d.]/g, "")) || 0;
      return out;
    })
    .filter((r) => r.name && String(r.name).trim());
}

export default function SoftwareMonitor() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState(null);
  const [unlisted, setUnlisted] = useState([]);
  const [tab, setTab] = useState("inventory");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s, u] = await Promise.all([api("/software"), api("/software/summary"), api("/software/unlisted")]);
      setList(l); setSummary(s); setUnlisted(u);
      setError("");
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const categories = useMemo(() => ["All", ...new Set((list || []).map((s) => s.category))], [list]);
  const filtered = useMemo(
    () =>
      (list || []).filter(
        (s) =>
          (cat === "All" || s.category === cat) &&
          `${s.name} ${s.vendor} ${s.owner} ${s.department}`.toLowerCase().includes(q.toLowerCase())
      ),
    [list, q, cat]
  );

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the inventory?`)) return;
    try { await api(`/software/${s.id}`, { method: "DELETE" }); load(); } catch (e) { setError(e.message); }
  };
  const checkNow = async (s) => {
    try { await api(`/software/${s.id}/check`, { method: "POST" }); load(); } catch (e) { setError(e.message); }
  };

  if (!list || !summary) return error ? <ErrorNote>{error}</ErrorNote> : <Spinner label="Loading inventory" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Software Monitor"
        subtitle="Every tool the company uses: who owns it, what it costs, when it renews and whether it is online."
        actions={
          isAdmin && (
            <>
              <Button variant="outline" onClick={() => setImporting(true)}><Upload size={15} /> Import form</Button>
              <Button onClick={() => setEditing({})}><Plus size={15} /> Add tool</Button>
            </>
          )
        }
      />

      <ErrorNote>{error}</ErrorNote>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <Stat label="Tools tracked" value={summary.total} icon={<Package size={18} />} hint={`${summary.approved} approved`} />
        <Stat
          label="Online / Down"
          value={`${summary.health.up} / ${summary.health.down}`}
          tone={summary.health.down ? "brand" : "good"}
          icon={<Activity size={18} />}
          hint={`${summary.health.unknown} not monitored`}
        />
        <Stat label="Monthly spend" value={money(summary.monthlySpend)} icon={<Wallet size={18} />} />
        <Stat
          label="Renewals ≤ 30 days"
          value={summary.renewals.length}
          tone={summary.renewals.length ? "warn" : "default"}
          icon={<CalendarClock size={18} />}
        />
      </div>

      {summary.renewals.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">Upcoming renewals</p>
            <p className="mt-0.5">
              {summary.renewals.map((r) => `${r.name} (${r.overdue ? "overdue, " : ""}${r.renewalDate})`).join(" · ")}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          ["inventory", `Inventory (${list.length})`],
          ["detected", `Detected on PCs (${unlisted.length} unlisted)`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === k ? "border-[#C00000] text-[#C00000]" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "inventory" && (
        <Card padded={false}>
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className={`${inputClass} pl-9`} placeholder="Search tools, vendors, owners…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select className={`${inputClass} sm:w-56`} value={cat} onChange={(e) => setCat(e.target.value)}>
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <Empty>No tools match.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3 font-medium">Tool</th>
                    <th className="px-5 py-3 font-medium">Owner</th>
                    <th className="px-5 py-3 font-medium">Seats</th>
                    <th className="px-5 py-3 font-medium">Cost / mo</th>
                    <th className="px-5 py-3 font-medium">Renews</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    {isAdmin && <th className="px-5 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <p className="font-medium text-[#111111]">
                          {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#C00000]">{s.name}</a> : s.name}
                          {!s.approved && <span className="ml-2"><Badge tone="warn">Unapproved</Badge></span>}
                        </p>
                        <p className="text-xs text-gray-500">{s.category} · {s.kind}{s.vendor ? ` · ${s.vendor}` : ""}</p>
                      </td>
                      <td className="px-5 py-3 text-gray-700">{s.owner || "—"}{s.department && <span className="block text-xs text-gray-500">{s.department}</span>}</td>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{s.seats || "—"}</td>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{s.monthlyCost ? money(s.monthlyCost, s.currency) : "—"}</td>
                      <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{s.renewalDate || "—"}</td>
                      <td className="px-5 py-3">
                        {statusBadge(s.status)}
                        {s.responseMs != null && s.status !== "unknown" && <span className="block text-xs text-gray-400 mt-0.5">{s.responseMs} ms</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-5 py-3">
                          <div className="flex justify-end gap-1">
                            {s.checkUrl && (
                              <button onClick={() => checkNow(s)} className="p-1.5 text-gray-400 hover:text-[#C00000]" title="Check now"><RefreshCw size={15} /></button>
                            )}
                            <button onClick={() => setEditing(s)} className="p-1.5 text-gray-400 hover:text-[#C00000]" title="Edit"><Pencil size={15} /></button>
                            <button onClick={() => remove(s)} className="p-1.5 text-gray-400 hover:text-[#C00000]" title="Remove"><Trash2 size={15} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "detected" && (
        <Card
          title="Installed programs not in your inventory"
          padded={false}
          action={<span className="text-xs text-gray-500">{summary.machinesReporting} PC(s) reporting</span>}
        >
          {summary.machinesReporting === 0 ? (
            <Empty>
              No PCs have reported yet. Deploy <code>dashboard-backend/scripts/windows-agent.ps1</code> to start detecting installed software.
            </Empty>
          ) : unlisted.length === 0 ? (
            <Empty>Everything installed on reporting PCs is in the inventory.</Empty>
          ) : (
            <ul className="divide-y divide-gray-100">
              {unlisted.map((u) => (
                <li key={u.name} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#111111] truncate">{u.name}</p>
                    <p className="text-xs text-gray-500 truncate">{u.publisher || "Unknown publisher"} · {u.machines.join(", ")}</p>
                  </div>
                  <Badge tone="warn">{u.machines.length} PC{u.machines.length > 1 ? "s" : ""}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {editing && (
        <ToolForm
          tool={editing}
          categories={categories.filter((c) => c !== "All")}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {importing && <ImportModal onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
    </div>
  );
}

function ToolForm({ tool, categories, onClose, onSaved }) {
  const isEdit = !!tool.id;
  const [f, setF] = useState({
    name: "", category: categories[0] || "Other", vendor: "", kind: "SaaS", owner: "", department: "",
    seats: 0, monthlyCost: 0, url: "", checkUrl: "", detectName: "", approved: true, notes: "",
    ...tool,
    renewalDate: tool.renewalDate || "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api(isEdit ? `/software/${tool.id}` : "/software", { method: isEdit ? "PUT" : "POST", body: f });
      onSaved();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  return (
    <Modal title={isEdit ? `Edit ${tool.name}` : "Add tool"} onClose={onClose} width="max-w-2xl">
      <form onSubmit={save} className="space-y-4">
        <ErrorNote>{error}</ErrorNote>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><input className={inputClass} value={f.name} onChange={(e) => set("name", e.target.value)} required /></Field>
          <Field label="Category">
            <input className={inputClass} list="sw-cats" value={f.category} onChange={(e) => set("category", e.target.value)} />
            <datalist id="sw-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Vendor"><input className={inputClass} value={f.vendor} onChange={(e) => set("vendor", e.target.value)} /></Field>
          <Field label="Type">
            <select className={inputClass} value={f.kind} onChange={(e) => set("kind", e.target.value)}>
              {KINDS.map((k) => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Owner"><input className={inputClass} value={f.owner} onChange={(e) => set("owner", e.target.value)} /></Field>
          <Field label="Department"><input className={inputClass} value={f.department} onChange={(e) => set("department", e.target.value)} /></Field>
          <Field label="Seats / users"><input type="number" min="0" className={inputClass} value={f.seats} onChange={(e) => set("seats", e.target.value)} /></Field>
          <Field label="Monthly cost (INR)"><input type="number" min="0" className={inputClass} value={f.monthlyCost} onChange={(e) => set("monthlyCost", e.target.value)} /></Field>
          <Field label="Renewal date"><input type="date" className={inputClass} value={f.renewalDate} onChange={(e) => set("renewalDate", e.target.value)} /></Field>
          <Field label="Open link"><input className={inputClass} value={f.url} onChange={(e) => set("url", e.target.value)} placeholder="https://" /></Field>
          <Field label="Health-check URL" hint="Leave empty if it cannot be pinged.">
            <input className={inputClass} value={f.checkUrl} onChange={(e) => set("checkUrl", e.target.value)} placeholder="https://" />
          </Field>
          <Field label="Detect as" hint="Text matched against installed program names.">
            <input className={inputClass} value={f.detectName} onChange={(e) => set("detectName", e.target.value)} placeholder={f.name} />
          </Field>
        </div>
        <Field label="Notes"><textarea rows={2} className={inputClass} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="accent-[#C00000]" checked={f.approved} onChange={(e) => set("approved", e.target.checked)} />
          Approved for company use
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>{isEdit ? "Save changes" : "Add tool"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function ImportModal({ onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setRows(null); setResult(null);
    setFileName(file.name);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const mapped = mapRows(json);
      if (!mapped.length) setError("No rows with a tool/software name were found. Check the first row contains column headings.");
      setRows(mapped);
    } catch {
      setError("Could not read that file. Use .xlsx or .csv exported from your form.");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      setResult(await api("/software/import", { method: "POST", body: { rows } }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <Modal title="Import software list from your form" onClose={onClose} width="max-w-xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Export the form responses (Google Forms: <em>Responses → Link to Sheets → Download as .xlsx</em>) and upload
          it here. Columns are matched by heading (name, vendor, owner, department, cost, renewal…). Tools that already
          exist are skipped.
        </p>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="block w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" />
        <ErrorNote>{error}</ErrorNote>

        {rows && rows.length > 0 && !result && (
          <div className="border border-gray-200 rounded-lg">
            <p className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
              {fileName}: {rows.length} tool(s) found. Preview:
            </p>
            <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 text-sm">
              {rows.slice(0, 30).map((r, i) => (
                <li key={i} className="px-4 py-2 flex justify-between gap-3">
                  <span className="font-medium text-[#111111] truncate">{r.name}</span>
                  <span className="text-gray-500 truncate">{r.vendor || r.category || ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result && (
          <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-700">
            Added {result.added} tool(s){result.skipped ? `, skipped ${result.skipped} duplicate(s)` : ""}.
          </div>
        )}

        <div className="flex justify-end gap-2">
          {result ? (
            <Button onClick={onDone}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} loading={busy} disabled={!rows?.length}>Import {rows?.length || ""}</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
